/**
 * One entry of a task version's `tools`, in any shape a plan or a save has stored: a bare tool name, or an object
 * naming it by `name`, `toolType` or `type`, with whatever else the entry carries (its own `gates`, say).
 *
 * os#1687: plans wrote entries as objects while the task page, plan apply and a run read each one as a string, so
 * 13 tasks showed "Task not found." and every edit on them threw `entry.indexOf is not a function`. This is the one
 * place an entry is turned into a tool name, so the readers cannot disagree about it again.
 */
export type CanonicalToolEntry = { name: string; [key: string]: unknown }

/**
 * An entry as a task version may hold it, in whichever shape it was written: rows written before the canonical shape
 * hold strings, objects or both. Read it through `toolEntryName`, never as a string.
 */
export type ToolEntry = string | { readonly [key: string]: unknown }

/** The keys an object entry may name its tool by, in the order they are read. No other key is guessed at. */
const NAME_KEYS = ['name', 'toolType', 'type'] as const

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** The tool name an entry holds: `name`, else `toolType`, else `type`; a bare string is its own name. `null` when none. */
export function toolEntryName(entry: unknown): string | null {
    if (typeof entry === 'string') {
        return entry === '' ? null : entry
    }
    if (!isRecord(entry)) {
        return null
    }
    for (const key of NAME_KEYS) {
        const value = entry[key]
        if (typeof value === 'string' && value !== '') {
            return value
        }
    }
    return null
}

/**
 * Whether an entry marks its tool optional (`{ name, optional: true }`): a run of the task goes ahead without it when no
 * binding serves it, and uses it when one does. A bare string, and an object without the flag, is required, as every
 * entry was before the flag.
 */
export function toolEntryOptional(entry: unknown): boolean {
    return isRecord(entry) && entry['optional'] === true
}

/** Every entry's tool name, in order, leaving out an entry that names none. */
export function toolEntryNames(entries: unknown): string[] {
    if (!Array.isArray(entries)) {
        return []
    }
    const names: string[] = []
    for (const entry of entries) {
        const name = toolEntryName(entry)
        if (name !== null) {
            names.push(name)
        }
    }
    return names
}

/** The first entry no tool name can be derived from, and where it sits; `null` when every entry is readable. */
export function unreadableToolEntry(entries: unknown): { index: number; entry: unknown } | null {
    if (!Array.isArray(entries)) {
        return null
    }
    const index = entries.findIndex(entry => toolEntryName(entry) === null)
    return index === -1 ? null : { index, entry: entries[index] }
}

/**
 * Every entry in the one stored shape: an object carrying its derived `name`, every other key kept as it was. An
 * entry no name can be derived from is left out, since nothing can call it; a write finds it first with
 * `unreadableToolEntry` and refuses it instead.
 */
export function canonicalToolEntries(entries: unknown): CanonicalToolEntry[] {
    if (!Array.isArray(entries)) {
        return []
    }
    const canonical: CanonicalToolEntry[] = []
    for (const entry of entries) {
        const name = toolEntryName(entry)
        if (name === null) {
            continue
        }
        canonical.push(isRecord(entry) ? { ...entry, name } : { name })
    }
    return canonical
}
